import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createDisplayOnlyUser } from '../../api/usersAPI';
import { showToastError, showToastSuccess } from '../../utils/errorController';
import { useValidator } from '../../utils/useValidator';

const CreateDisplayOnlyUserModal = ({
  show = false,
  onClose,
  onSuccess,
  departmentId,
} = {}) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const { errors, validateName, validateAll, resetErrors, setErrors } = useValidator();

  useEffect(() => {
    if (!show) {
      setName('');
      setSaving(false);
      resetErrors();
    }
  }, [resetErrors, show]);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [onClose, saving]);

  const handleNameChange = useCallback(
    (event) => {
      const { value } = event.target;
      setName(value);
      if (errors.name) {
        validateName(value);
      }
    },
    [errors.name, validateName],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const trimmedName = typeof name === 'string' ? name.trim() : '';
      if (!validateAll({ name: trimmedName })) {
        return;
      }

      setSaving(true);
      try {
        const payload = {
          name: trimmedName,
        };
        if (typeof departmentId === 'number' && !Number.isNaN(departmentId)) {
          payload.departmentId = departmentId;
        }
        const result = await createDisplayOnlyUser(payload);
        if (!result) {
          showToastError('Unable to create crew member.');
          return;
        }
        showToastSuccess('Crew member created.');
        onSuccess?.(result);
        setName('');
        setErrors({});
        onClose?.();
      } catch (error) {
        console.error('Failed to create display only user', error);
        const message =
          error?.response?.data?.message ||
          (Array.isArray(error?.response?.data?.message)
            ? error.response.data.message.join(', ')
            : null);
        if (message) {
          showToastError(message);
        } else {
          showToastError('Unable to create crew member.');
        }
      } finally {
        setSaving(false);
      }
    },
    [departmentId, name, onClose, onSuccess, setErrors, validateAll],
  );

  if (!show) return null;

  return (
    <div className="modal modal-backdrop show d-block" style={{ zIndex: "2000" }} tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Crew Member</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleClose}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label" htmlFor="display-only-user-name">
                  Name
                </label>
                <input
                  id="display-only-user-name"
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={name}
                  onChange={handleNameChange}
                  onBlur={() => validateName(name)}
                  disabled={saving}
                  autoFocus
                />
                {errors.name ? (
                  <div className="invalid-feedback">{errors.name}</div>
                ) : null}
                <div className="form-text">
                  Name must be at least 3 characters and contain no special characters.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

CreateDisplayOnlyUserModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  departmentId: PropTypes.number,
};

export default CreateDisplayOnlyUserModal;
